import { useState } from 'react'

import { navigateTo } from '../../../../../common/frontend/route-utils.js'
import { t } from '../../../../../common/i18n.js'
import { Language } from '../../../../../common/types/i18n.js'
import { Button } from '../../../../components/button/button.js'
import { Select } from '../../../../components/forms/select/select.js'
import { TextField } from '../../../../components/forms/text-field/text-field.js'
import {
    LogOut,
    Plus,
    Search,
    Structure,
    User,
    UserPlus,
    X,
} from '../../../../components/icon/solar-icons.js'
import '../../../../components/portal-avatar/portal-avatar.css'
import { PortalAvatar } from '../../../../components/portal-avatar/portal-avatar.js'
import '../../../../components/portal-local-time/portal-local-time.css'
import { PortalLocalTime } from '../../../../components/portal-local-time/portal-local-time.js'
import { PortalOrgChart } from '../../../../components/portal-org-chart/portal-org-chart.js'
import {
    FilterPopover,
    SortPopover,
} from '../../../../components/portal-people-filters/portal-people-filters.js'
import type {
    FilterOptions,
    PeopleFilters,
    SortState,
} from '../../../../components/portal-people-filters/types.js'
import '../../../../components/portal-tag/portal-tag.css'
import { PortalTag } from '../../../../components/portal-tag/portal-tag.js'
import { Tooltip } from '../../../../components/tooltip/tooltip.js'
import { View } from '../../../../types.js'
import { formatDaysUntil, getDisplayName, splitList } from '../../utils/format.js'
import type { UpcomingEvent } from '../../utils/portal-api.js'
import { eventIcon, eventLabel } from './event-format.js'
import { EventsTab } from './events-tab.js'
import { PeopleLayout } from './people-layout.js'
import type { PeopleTab } from './people-types.js'
import './people-view.css'
import { buildRowEventMap, selectRowBadge } from './row-badge.js'

export interface EmployeeListItem {
    id: number
    first_name: string
    last_name: string
    avatar_url: string | null
    job_title: string | null
    email: string | null
    location_name: string | null
    country_code: string | null
    location_timezone: string | null
    manager_id: number | null
    team_names: string | null
    company_slugs: string
    /** Active employment record's collaboration form; null without an active record. */
    collaboration_type: string | null
    status: string
    hire_date: string | null
    /** Known last day — null unless the viewer holds `see_new_hires`. */
    employment_end_date: string | null
}

interface RefOption {
    id: number
    name: string
}

export interface PeopleViewProps {
    view: View
    lang: Language
    /** Holds the `add_members` profile permission — shows the create button/form. */
    canAddMembers: boolean
    /** Holds the `see_offboarded_users` profile permission — shows the Offboarded tab. */
    canSeeOffboarded: boolean

    activeTab: PeopleTab
    onTabChange: (tab: PeopleTab) => void
    activeCount: number
    eventsCount: number

    // Active list
    employees: EmployeeListItem[]
    isLoading: boolean
    searchQuery: string
    onSearch: (query: string) => void
    filters: PeopleFilters
    onFiltersChange: (filters: PeopleFilters) => void
    filterOptions: FilterOptions
    sort: SortState
    onSortChange: (sort: SortState) => void

    // Events
    events: UpcomingEvent[]
    isLoadingEvents: boolean

    // Create
    showCreateForm: boolean
    onToggleCreateForm: (open: boolean) => void
    onCreateEmployee: (data: CreateEmployeeData) => Promise<boolean>
    companies: RefOption[]
    teams: RefOption[]

    // Org chart — full active roster (independent of the list's filters)
    showOrgChart: boolean
    onToggleOrgChart: (open: boolean) => void
    orgChartEmployees: EmployeeListItem[]
}

export interface CreateEmployeeData {
    first_name: string
    last_name: string
    company_id: number
    team_ids?: number[]
    job_title?: string
    work_email?: string
    hire_date?: string
}

/** Localised country name from an ISO 3166-1 alpha-2 code, e.g. "EE" → "Estonia". */
const countryName = (code: string | null, lang: Language): string | null => {
    if (!code) return null
    try {
        return new Intl.DisplayNames([lang], { type: 'region' }).of(code) ?? code
    } catch {
        return code
    }
}

/** Current UTC offset (in minutes) for an IANA timezone, or null if it's invalid. */
const currentOffsetMinutes = (timeZone: string): number | null => {
    try {
        const now = new Date()
        const utc = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }))
        const zoned = new Date(now.toLocaleString('en-US', { timeZone }))
        return Math.round((zoned.getTime() - utc.getTime()) / 60_000)
    } catch {
        return null
    }
}

export const PeopleView = ({
    lang,
    canAddMembers,
    canSeeOffboarded,
    activeTab,
    onTabChange,
    activeCount,
    eventsCount,
    employees,
    isLoading,
    searchQuery,
    onSearch,
    filters,
    onFiltersChange,
    filterOptions,
    sort,
    onSortChange,
    events,
    isLoadingEvents,
    showCreateForm,
    onToggleCreateForm,
    onCreateEmployee,
    companies,
    teams,
    showOrgChart,
    onToggleOrgChart,
    orgChartEmployees,
}: PeopleViewProps) => {
    const ph = t.portal

    // Soonest upcoming event per person, derived from the already-fetched events
    // feed; together with `today` it drives each row's inline lifecycle badge.
    const rowEvents = buildRowEventMap(events)
    const today = new Date()

    // Viewer's own current UTC offset — used to hide the local-time clock for
    // colleagues who are on the same time as the viewer right now.
    const viewerOffset = -new Date().getTimezoneOffset()

    const handleRowClick = (id: number) => {
        navigateTo(`/people/${id}`)
    }

    const tabs: { id: PeopleTab; label: string; count: number | null }[] = [
        { id: 'active', label: ph.people.activeTab(lang), count: activeCount },
        { id: 'events', label: ph.people.eventsTab(lang), count: eventsCount },
        // Offboarded employees are manager/admin-only.
        ...(canSeeOffboarded
            ? [{ id: 'offboarded' as PeopleTab, label: ph.people.offboardedTab(lang), count: null }]
            : []),
    ]

    const renderRow = (emp: EmployeeListItem) => {
        const teamList = splitList(emp.team_names, ',')
        const companyLabel = splitList(emp.company_slugs, ',')
            .map((c) => c.toUpperCase())
            .join(', ')
        const jobTitle = emp.job_title?.trim()
        const country = countryName(emp.country_code, lang)
        const locationLabel = [emp.location_name, country].filter(Boolean).join(', ')
        // Show the local time only for people in a different timezone than the viewer.
        const empOffset = emp.location_timezone ? currentOffsetMinutes(emp.location_timezone) : null
        const showLocalTime = empOffset !== null && empOffset !== viewerOffset
        const badge = selectRowBadge(
            emp.hire_date,
            emp.employment_end_date,
            rowEvents.get(emp.id),
            today,
        )

        return (
            <div key={emp.id} className="portal-people__row" onClick={() => handleRowClick(emp.id)}>
                <PortalAvatar
                    name={`${emp.first_name} ${emp.last_name}`}
                    avatarUrl={emp.avatar_url}
                    px={44}
                />
                <div className="portal-people__row-id">
                    <div className="portal-people__row-name">
                        {getDisplayName(emp)}
                        {badge?.kind === 'joining' && (
                            <span className="portal-people__event-badge portal-people__event-badge--joining">
                                <UserPlus size={16} />
                                {`${ph.people.joiningBadge(lang)} ${formatDaysUntil(badge.daysUntil, lang)}`}
                            </span>
                        )}
                        {badge?.kind === 'leaving' && (
                            <span className="portal-people__event-badge portal-people__event-badge--offboarding">
                                <LogOut size={16} />
                                {`${ph.people.leavingBadge(lang)} ${formatDaysUntil(badge.daysUntil, lang)}`}
                            </span>
                        )}
                        {badge?.kind === 'event' && (
                            <span
                                className={`portal-people__event-badge portal-people__event-badge--${badge.event.type}`}
                            >
                                {eventIcon(badge.event.type)}
                                {`${eventLabel(badge.event, lang)} ${formatDaysUntil(badge.event.daysUntil, lang)}`}
                            </span>
                        )}
                    </div>
                    <div className="portal-people__row-sub">
                        {jobTitle}
                        {jobTitle && companyLabel ? ` ${ph.people.inCompany(lang)} ` : null}
                        {companyLabel && (
                            <span className="portal-people__row-company">{companyLabel}</span>
                        )}
                        {!jobTitle && !companyLabel && '—'}
                    </div>
                </div>

                {/* Location + local time, shown only for people in a different
                    timezone than the viewer. The wrapper stays (empty) so the
                    grid columns line up across rows. */}
                <div className="portal-people__row-loc">
                    {showLocalTime && (
                        <>
                            {locationLabel && (
                                <div className="portal-people__row-loc-name">{locationLabel}</div>
                            )}
                            {emp.location_timezone && (
                                <PortalLocalTime
                                    timezone={emp.location_timezone}
                                    className="portal-people__row-time"
                                />
                            )}
                        </>
                    )}
                </div>

                <div className="portal-people__row-tags">
                    {teamList.map((team) => (
                        <PortalTag key={`t-${team}`} label={team} variant="team" />
                    ))}
                </div>
            </div>
        )
    }

    // Search stays out of the title's actions slot on the events tab (same as
    // before), so it keeps its top-right position only where it applies.
    const search =
        activeTab !== 'events' ? (
            <TextField
                className="portal-people__search"
                size="small"
                prefix={
                    <span className="portal-people__search-icon">
                        <Search size={14} />
                    </span>
                }
                placeholder={ph.people.search(lang)}
                value={searchQuery}
                onChange={(value) => onSearch(value)}
            />
        ) : undefined

    const tabItems = tabs.map((tab) => ({
        key: tab.id,
        label: tab.label,
        count: tab.count,
    }))

    const toolbar = (
        <>
            <OrgChartButton lang={lang} onClick={() => onToggleOrgChart(true)} />
            {activeTab !== 'events' && (
                <>
                    <FilterPopover
                        lang={lang}
                        filters={filters}
                        options={filterOptions}
                        onChange={onFiltersChange}
                    />
                    <SortPopover lang={lang} sort={sort} onChange={onSortChange} />
                </>
            )}
            {canAddMembers && (
                <Button
                    className="portal-people__create-btn"
                    size="small"
                    variant="primary"
                    iconLeft={Plus}
                    text={ph.people.createBtn(lang)}
                    onClick={() => onToggleCreateForm(true)}
                />
            )}
        </>
    )

    const content =
        activeTab === 'events' ? (
            <EventsTab events={events} isLoading={isLoadingEvents} lang={lang} />
        ) : isLoading && employees.length === 0 ? (
            <div className="portal-people__empty">
                <div className="portal-people__empty-text">{ph.profile.loading(lang)}</div>
            </div>
        ) : employees.length === 0 ? (
            <div className="portal-people__empty">
                <div className="portal-people__empty-icon">
                    <User size={32} />
                </div>
                <div className="portal-people__empty-text">{t.common.noResults(lang)}</div>
            </div>
        ) : (
            <div className="portal-people__list">{employees.map(renderRow)}</div>
        )

    return (
        <>
            <PeopleLayout
                lang={lang}
                title={ph.people.title(lang)}
                search={search}
                tabItems={tabItems}
                activeTab={activeTab}
                onTabChange={(key) => onTabChange(key as PeopleTab)}
                toolbar={toolbar}
            >
                {content}
            </PeopleLayout>

            {/* Create Employee Slide-Over */}
            {showCreateForm && (
                <CreateEmployeeSlideOver
                    lang={lang}
                    companies={companies}
                    teams={teams}
                    onClose={() => onToggleCreateForm(false)}
                    onSubmit={onCreateEmployee}
                />
            )}

            {/* Org chart full-screen overlay */}
            {showOrgChart && (
                <PortalOrgChart
                    employees={orgChartEmployees}
                    onNavigate={(path) => {
                        onToggleOrgChart(false)
                        navigateTo(path)
                    }}
                    onClose={() => onToggleOrgChart(false)}
                    labels={{
                        title: (n) => ph.people.orgChartTitle(n)(lang),
                        leave: ph.people.orgChartLeave(lang),
                        reports: (n) => ph.people.orgChartReports(n)(lang),
                        zoomIn: ph.people.orgChartZoomIn(lang),
                        zoomOut: ph.people.orgChartZoomOut(lang),
                        fit: ph.people.orgChartFit(lang),
                        allTeams: ph.people.orgChartAllTeams(lang),
                        teamFilter: ph.people.orgChartTeamFilter(lang),
                    }}
                />
            )}
        </>
    )
}

// ── Org chart toolbar button (with hover tooltip) ──

const OrgChartButton = ({ lang, onClick }: { lang: Language; onClick: () => void }) => {
    const ph = t.portal
    const [tipShown, setTipShown] = useState(false)

    return (
        <Tooltip
            element="span"
            triggerEvent="hover"
            placement="bottom"
            triggerClassName="portal-people__org-chart-tip-trigger"
            isShown={tipShown}
            onToggle={setTipShown}
            trigger={
                <Button
                    size="small"
                    variant="secondary"
                    iconOnly
                    iconLeft={Structure}
                    text={ph.people.orgChartTooltipTitle(lang)}
                    onClick={onClick}
                />
            }
        >
            <div className="portal-people__org-chart-tip">
                <strong>{ph.people.orgChartTooltipTitle(lang)}</strong>
                <span>{ph.people.orgChartTooltipDesc(lang)}</span>
            </div>
        </Tooltip>
    )
}

// ── Create Employee Slide-Over ──

interface CreateSlideOverProps {
    lang: Language
    companies: RefOption[]
    teams: RefOption[]
    onClose: () => void
    onSubmit: (data: CreateEmployeeData) => Promise<boolean>
}

const CreateEmployeeSlideOver = ({
    lang,
    companies,
    teams,
    onClose,
    onSubmit,
}: CreateSlideOverProps) => {
    const ph = t.portal

    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [companyId, setCompanyId] = useState<number | ''>(companies[0]?.id ?? '')
    const [teamId, setTeamId] = useState<number | ''>('')
    const [jobTitle, setJobTitle] = useState('')
    const [workEmail, setWorkEmail] = useState('')
    const [hireDate, setHireDate] = useState(new Date().toISOString().slice(0, 10))
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const canSubmit = firstName.trim() && lastName.trim() && workEmail.trim() && companyId !== ''

    const handleSubmit = async () => {
        if (!canSubmit || isSubmitting) return
        setIsSubmitting(true)
        setError(null)

        const data: CreateEmployeeData = {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            company_id: Number(companyId),
            ...(teamId !== '' && { team_ids: [Number(teamId)] }),
            ...(jobTitle.trim() && { job_title: jobTitle.trim() }),
            ...(workEmail.trim() && { work_email: workEmail.trim() }),
            ...(hireDate && { hire_date: hireDate }),
        }

        const ok = await onSubmit(data)
        if (!ok) {
            setError(ph.people.createError(lang))
            setIsSubmitting(false)
        }
    }

    return (
        <>
            <div className="portal-slide-overlay" onClick={onClose} />
            <div className="portal-slide-over">
                <div className="portal-slide-over__header">
                    <h3 className="portal-slide-over__title">{ph.people.createTitle(lang)}</h3>
                    <Button
                        variant="tertiary"
                        size="small"
                        iconOnly
                        iconLeft={X}
                        text={t.portal.settings.close(lang)}
                        onClick={onClose}
                    />
                </div>

                <div className="portal-slide-over__body">
                    {error && <div className="portal-slide-over__error">{error}</div>}

                    <TextField
                        wrapperClass="portal-slide-over__field"
                        label={`${ph.people.firstName(lang)} *`}
                        value={firstName}
                        onChange={(value) => setFirstName(value)}
                        inputProps={{ autoFocus: true }}
                    />

                    <TextField
                        wrapperClass="portal-slide-over__field"
                        label={`${ph.people.lastName(lang)} *`}
                        value={lastName}
                        onChange={(value) => setLastName(value)}
                    />

                    <Select
                        wrapperClass="portal-slide-over__field"
                        label={`${ph.people.companyLabel(lang)} *`}
                        placeholder={ph.people.selectCompany(lang)}
                        value={companyId === '' ? '' : String(companyId)}
                        onChange={(value) => setCompanyId(value ? Number(value) : '')}
                        options={companies.map((c) => ({ value: String(c.id), label: c.name }))}
                    />

                    <Select
                        wrapperClass="portal-slide-over__field"
                        label={ph.people.team(lang)}
                        placeholder={ph.people.selectTeam(lang)}
                        value={teamId === '' ? '' : String(teamId)}
                        onChange={(value) => setTeamId(value ? Number(value) : '')}
                        options={teams.map((tm) => ({ value: String(tm.id), label: tm.name }))}
                    />

                    <TextField
                        wrapperClass="portal-slide-over__field"
                        label={ph.people.jobTitle(lang)}
                        value={jobTitle}
                        onChange={(value) => setJobTitle(value)}
                    />

                    <TextField
                        wrapperClass="portal-slide-over__field"
                        label={`${ph.people.workEmail(lang)} *`}
                        value={workEmail}
                        onChange={(value) => setWorkEmail(value)}
                        inputProps={{ type: 'email' }}
                    />

                    <TextField
                        wrapperClass="portal-slide-over__field"
                        label={ph.people.hireDateLabel(lang)}
                        value={hireDate}
                        onChange={(value) => setHireDate(value)}
                        inputProps={{ type: 'date' }}
                    />
                </div>

                <div className="portal-slide-over__footer">
                    <Button
                        size="small"
                        variant="tertiary"
                        text={t.common.cancel(lang)}
                        isDisabled={isSubmitting}
                        onClick={onClose}
                    />
                    <Button
                        size="small"
                        variant="primary"
                        text={isSubmitting ? ph.people.creating(lang) : ph.people.createBtn(lang)}
                        isDisabled={!canSubmit || isSubmitting}
                        onClick={() => void handleSubmit()}
                    />
                </div>
            </div>
        </>
    )
}
